// User and Authentication Types
export interface User {
  id: string;
  tenant_id: string;
  email: string;
  auth_provider_id: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  avatar_url?: string;
  profilePictureUrl?: string;
  phone?: string;
  is_active: boolean;
  last_login_at?: string;
  isKlijentliAdmin?: boolean;
  created_at: string;
  updated_at: string;
}

export type UserRole = 'admin' | 'manager' | 'therapist' | 'receptionist';

// Role-based permissions
export interface RolePermissions {
  // Client Management
  canViewClients: boolean;
  canCreateClients: boolean;
  canEditClients: boolean;
  canDeleteClients: boolean;
  
  // Treatment Management
  canViewTreatments: boolean;
  canCreateTreatments: boolean;
  canEditTreatments: boolean;
  canDeleteTreatments: boolean;
  
  // Appointment Management
  canViewAppointments: boolean;
  canCreateAppointments: boolean;
  canEditAppointments: boolean;
  canDeleteAppointments: boolean;
  
  // Reports & Analytics
  canViewReports: boolean;
  canExportReports: boolean;
  
  // User Management
  canViewUsers: boolean;
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canManageRoles: boolean;
  
  // Organization Management
  canViewSettings: boolean;
  canEditSettings: boolean;
  canManageLocations: boolean;
  canManageSubscription: boolean;
  
  // Financial Management
  canViewFinancials: boolean;
  canManageBilling: boolean;
  
  // System Administration
  canAccessSystemLogs: boolean;
  canManageIntegrations: boolean;
}

// Role hierarchy and permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  admin: {
    // Full access to everything
    canViewClients: true,
    canCreateClients: true,
    canEditClients: true,
    canDeleteClients: true,
    canViewTreatments: true,
    canCreateTreatments: true,
    canEditTreatments: true,
    canDeleteTreatments: true,
    canViewAppointments: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: true,
    canViewReports: true,
    canExportReports: true,
    canViewUsers: true,
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canManageRoles: true,
    canViewSettings: true,
    canEditSettings: true,
    canManageLocations: true,
    canManageSubscription: true,
    canViewFinancials: true,
    canManageBilling: true,
    canAccessSystemLogs: true,
    canManageIntegrations: true,
  },
  manager: {
    // Location management and oversight
    canViewClients: true,
    canCreateClients: true,
    canEditClients: true,
    canDeleteClients: true,
    canViewTreatments: true,
    canCreateTreatments: true,
    canEditTreatments: true,
    canDeleteTreatments: true,
    canViewAppointments: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: true,
    canViewReports: true,
    canExportReports: true,
    canViewUsers: true,
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: false,
    canManageRoles: false,
    canViewSettings: true,
    canEditSettings: true,
    canManageLocations: false,
    canManageSubscription: false,
    canViewFinancials: true,
    canManageBilling: false,
    canAccessSystemLogs: false,
    canManageIntegrations: false,
  },
  therapist: {
    // Clinical access with limited administrative functions
    canViewClients: true,
    canCreateClients: true,
    canEditClients: true,
    canDeleteClients: false,
    canViewTreatments: true,
    canCreateTreatments: true,
    canEditTreatments: true,
    canDeleteTreatments: false,
    canViewAppointments: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: false,
    canViewReports: true,
    canExportReports: false,
    canViewUsers: false,
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canManageRoles: false,
    canViewSettings: false,
    canEditSettings: false,
    canManageLocations: false,
    canManageSubscription: false,
    canViewFinancials: false,
    canManageBilling: false,
    canAccessSystemLogs: false,
    canManageIntegrations: false,
  },
  receptionist: {
    // Front desk operations
    canViewClients: true,
    canCreateClients: true,
    canEditClients: false,
    canDeleteClients: false,
    canViewTreatments: true,
    canCreateTreatments: false,
    canEditTreatments: false,
    canDeleteTreatments: false,
    canViewAppointments: true,
    canCreateAppointments: true,
    canEditAppointments: true,
    canDeleteAppointments: false,
    canViewReports: false,
    canExportReports: false,
    canViewUsers: false,
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canManageRoles: false,
    canViewSettings: false,
    canEditSettings: false,
    canManageLocations: false,
    canManageSubscription: false,
    canViewFinancials: false,
    canManageBilling: false,
    canAccessSystemLogs: false,
    canManageIntegrations: false,
  },
};

// Tenant Types
export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  logo_url?: string;
  brand_colors?: {
    primary: string;
    secondary: string;
  };
  subscription_plan: SubscriptionPlan;
  subscription_status: SubscriptionStatus;
  stripe_customer_id?: string;
  trial_ends_at?: string;
  created_at: string;
  updated_at: string;
}

export type SubscriptionPlan = 'starter' | 'professional' | 'clinic';
export type SubscriptionStatus = 'trial' | 'active' | 'past_due' | 'canceled';

// Client Types
export interface Client {
  id: string;
  locationId: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: Gender;
  profilePictureUrl?: string;
  address?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  visitCount?: number;
  lastVisitDate?: string;
  totalSpent?: number;
  location?: {
    id: string;
    name: string;
  };
}

export type Gender = 'M' | 'F' | 'Other';
export type SkinType = 'normal' | 'oily' | 'dry' | 'combination' | 'sensitive';

// Location Types
export interface Location {
  id: string;
  tenant_id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Treatment Types
export interface TreatmentCategory {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  color?: string;
  created_at: string;
}

export interface TreatmentType {
  id: string;
  companyId?: string;
  name: string;
  description?: string;
  defaultDuration?: number;
  defaultCost?: number;
  isActive?: boolean;
  createdAt?: string;
}

export interface Treatment {
  id: string;
  tenant_id: string;
  category_id?: string;
  name: string;
  description?: string;
  duration_minutes?: number;
  price?: number;
  is_active: boolean;
  created_at: string;
}

// Treatment Record (actual treatment session/record)
export interface TreatmentRecord {
  id: string;
  locationId: string;
  visitId: string;
  clientId: string;
  treatmentType?: string; // Backward compatibility - string name
  treatmentTypeName?: string; // Display name(s) - comma-separated if multiple
  treatmentTypeIds?: string[]; // Array of treatment type IDs
  treatmentTypes?: TreatmentType[]; // Populated treatment type objects
  treatmentDate?: string;
  duration?: number;
  cost?: number;
  status: string;
  therapist?: string;
  notes?: string;
  observations?: string;
  measurements?: string;
  products?: string;
  homecare?: string;
  order?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface TreatmentSession {
  id: string;
  tenant_id: string;
  client_id: string;
  treatment_id: string;
  therapist_id: string;
  session_date: string;
  duration_minutes?: number;
  price?: number;
  notes?: string;
  skin_assessment?: Record<string, any>;
  products_used?: Array<{
    name: string;
    amount: string;
    notes: string;
  }>;
  observations?: string;
  next_appointment_recommendation?: number;
  homecare_instructions?: string;
  created_at: string;
  updated_at: string;
}

// Photo Types
export interface ClientPhoto {
  id: string;
  tenant_id: string;
  client_id: string;
  session_id?: string;
  file_path: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  photo_type: PhotoType;
  body_area?: string;
  angle?: string;
  notes?: string;
  taken_at: string;
  uploaded_by: string;
  created_at: string;
}

export type PhotoType = 'before' | 'after' | 'progress' | 'reference';

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// Form Types
export interface LoginForm {
  email: string;
  password: string;
}

export interface SignupForm {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  tenant_name: string;
}

export interface TenantSelectionForm {
  tenant_id: string;
}

// Context Types
export interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  location: Location | null;
  availableLocations: Location[];
  isLoading: boolean;
  isAuthenticated: boolean;
  showLocationSelector: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (form: SignupForm) => Promise<string>;
  logout: () => void;
  selectTenant: (tenantId: string) => Promise<void>;
  selectLocation: (locationId: string) => Promise<void>;
  refreshCurrentLocation: () => Promise<void>;
  fetchAvailableLocations: (userId: string) => Promise<Location[]>;
  createLocation: (locationData: Partial<Location>) => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  acceptInvitation: (token: string, userData: any) => Promise<void>;
  sendEmailVerification: (email: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
}

// Invitation Types
export interface Invitation {
  id: string;
  tenant_id: string;
  location_id?: string;
  email: string;
  role: UserRole;
  invited_by: string;
  status: InvitationStatus;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  updated_at: string;
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

export interface InvitationForm {
  email: string;
  role: UserRole;
  location_id?: string;
  expires_in_days?: number;
}

// Location Access Types
export interface LocationAccess {
  id: string;
  user_id: string;
  location_id: string;
  role: UserRole;
  granted_by: string;
  granted_at: string;
  is_active: boolean;
}

export interface LocationAccessForm {
  user_id: string;
  location_id: string;
  role: UserRole;
}

// Navigation Types
export interface NavItem {
  name: string;
  href: string;
  icon: string;
  current?: boolean;
}
